import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {ResizeObserver | null} */
  #layoutObserver = null;

  /** @type {number} */
  #rafId = 0;

  /** @type {number} */
  #barHeight = 0;

  /** @type {number} */
  #dock = 0;

  /** @type {number} */
  #bottomInset = 0;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {Element | null} */
  #buyButtonsBlock = null;

  /** @type {Element | null} */
  #footer = null;

  /** @type {Element | null} */
  #header = null;

  connectedCallback() {
    super.connectedCallback();

    this.#watchViewport();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#layoutObserver?.disconnect();
    this.#abortController.abort();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Wires up the triggers that recompute the bar's visibility.
   *
   * Deliberately not IntersectionObserver. An IO only calls back when its
   * target crosses a threshold, and the thresholds have to line up exactly
   * with the geometry the callback then tests — which they didn't: the footer
   * observer's 200px rootMargin fired while the footer was still 200px below
   * the fold, where "is the footer visible" is false, and nothing fired again
   * once it really appeared, so the bar sat on top of the footer. Reading live
   * geometry once per scroll frame has no thresholds to misalign, and gives
   * the same answer no matter which trigger fired.
   *
   * The scroll listener is registered in the capture phase because scroll
   * events don't bubble and this theme has two scroll containers: the document
   * on mobile and `.page-wrapper` at >=990px (see scroll-container.js).
   * Capturing on window catches both, so nothing has to be rebound when the
   * viewport crosses that breakpoint.
   */
  #watchViewport() {
    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#scheduleVisibilityUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#scheduleVisibilityUpdate, { passive: true, signal });

    // Layout can move the footer with no scroll at all — an accordion opening,
    // media finishing loading, a section re-rendering. Watching the bar itself
    // keeps #barHeight (the footer clearance) honest across breakpoints.
    this.#layoutObserver = new ResizeObserver(this.#handleLayoutChange);
    this.#layoutObserver.observe(this.refs.stickyBar);
    const main = document.querySelector('main');
    if (main) this.#layoutObserver.observe(main);

    this.#scheduleVisibilityUpdate();
  }

  /**
   * @param {ResizeObserverEntry[]} entries
   */
  #handleLayoutChange = (entries) => {
    for (const entry of entries) {
      if (entry.target !== this.refs.stickyBar) continue;
      // Border box, not contentRect: the bar's own padding is part of the
      // footprint the footer has to clear.
      this.#barHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
      // How far the bar floats off the bottom of the viewport — 20px on
      // desktop, 0 on mobile where it goes flush. Read here rather than per
      // frame: it only changes when the breakpoint does, which is exactly
      // when this fires.
      this.#bottomInset = parseFloat(getComputedStyle(this.refs.stickyBar).bottom) || 0;
    }
    this.#scheduleVisibilityUpdate();
  };

  /**
   * Coalesces every trigger into at most one geometry read per frame.
   */
  #scheduleVisibilityUpdate = () => {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.#updateStickyVisibility();
    });
  };

  /**
   * Recomputes where the sticky bar sits and whether it should be shown, from
   * the current geometry of the buy buttons and the footer. Stateless and
   * idempotent: the same viewport always yields the same answer, so it is safe
   * to call from any trigger, any number of times.
   */
  #updateStickyVisibility = () => {
    const buyButtonsBlock = this.#getBuyButtonsBlock();
    if (!buyButtonsBlock) {
      this.#hideStickyBar();
      return;
    }

    // Show once the CTAs have gone behind the pinned header — NOT once they
    // cross the viewport's own top edge. The header covers the top of the
    // viewport, so the buttons leave the shopper's view first and reach y=0
    // afterwards; measuring against 0 left a dead zone of 60px (desktop) /
    // 132px (mobile) where neither the real button nor the bar was reachable.
    //
    // Testing `bottom` rather than `top` keeps the bar hidden while the buy
    // buttons are merely still below the fold — it only stands in for CTAs
    // the shopper has actually scrolled past.
    const headerOverlap = this.#getHeaderOverlap();
    const ctaPassed = buyButtonsBlock.getBoundingClientRect().bottom <= headerOverlap;

    const viewportHeight = window.innerHeight;
    const footer = this.#getFooter();
    const footerTop = footer ? footer.getBoundingClientRect().top : Infinity;

    // Ride up on the footer instead of disappearing under it. Once the
    // footer's top edge is inside the viewport, lifting the bar by exactly
    // how far it has intruded parks the bar's bottom edge on that edge: the
    // two can never overlap, and the bar stays useful for the rest of the
    // page instead of being spent in one thumb-flick.
    //
    // Clamped so the climb stops at the header. Unclamped it kept going and
    // slid under the pinned header — and since the bar outranks the header on
    // z-index, it painted straight over it.
    const maxDock = Math.max(0, viewportHeight - this.#bottomInset - this.#barHeight - headerOverlap);
    this.#setDock(Math.min(Math.max(0, viewportHeight - footerTop), maxDock));

    // Give up at exactly the point that clamp starts to bite — where the
    // docked bar's top edge would meet the bottom of the header, i.e. the
    // whole gap between header and footer has closed and there is nowhere
    // left to stand. Derived from the same geometry rather than a tuned
    // fraction of the viewport, so it holds at any header size or viewport.
    const noRoomLeft = footerTop <= headerOverlap + this.#bottomInset + this.#barHeight;

    // Leave instantly in that case rather than over 300ms. The clamp holds the
    // bar still while the footer keeps climbing, so a fade here means the
    // footer spends the whole animation rising through a bar that is still
    // 50-80% opaque. Nothing to animate away from anyway: this only fires with
    // the bar wedged between header and footer.
    this.refs.stickyBar.toggleAttribute('data-no-room', noRoomLeft);

    if (ctaPassed && !noRoomLeft) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  };

  /**
   * Lifts the bar off the bottom of the viewport by `dock` pixels.
   *
   * Written as a custom property consumed by `translate` (not `transform`,
   * which the show/hide animation owns, and deliberately not transitioned) so
   * the bar tracks the footer frame-for-frame instead of easing after it.
   *
   * @param {number} dock
   */
  #setDock(dock) {
    const next = Math.round(dock);
    if (next === this.#dock) return;
    this.#dock = next;
    this.refs.stickyBar.style.setProperty('--sticky-atc-dock', `${next}px`);
    this.refs.stickyBar.toggleAttribute('data-docked', next > 0);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const targetAddToCartButton = this.#getTargetAddToCartButton();
    if (!targetAddToCartButton) return;
    targetAddToCartButton.dataset.puppet = 'true';
    targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Forwards a click to the main buy-box quantity selector's plus button, so
   * the sticky bar's own stepper (shown once the real one scrolls out of
   * view) drives the same quantity that gets added to the cart.
   */
  increaseStickyQuantity = () => {
    this.#getMainQuantitySelector()?.refs.plusButton?.click();
  };

  /**
   * Forwards a click to the main buy-box quantity selector's minus button.
   */
  decreaseStickyQuantity = () => {
    this.#getMainQuantitySelector()?.refs.minusButton?.click();
  };

  /**
   * Gets the quantity-selector-component instance from the main buy box.
   * @returns {(Component<{quantityInput: HTMLInputElement, minusButton: HTMLButtonElement, plusButton: HTMLButtonElement}>) | null}
   */
  #getMainQuantitySelector() {
    const productForm = this.#getProductForm();
    return productForm?.querySelector('quantity-selector-component[ref="quantitySelector"]') ?? null;
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // `childrenOnly` leaves the bar's own attributes alone, but re-assert
        // the state from the component rather than reading it back out of the
        // DOM, so #isStuck and data-stuck can never disagree after a morph.
        this.refs.stickyBar.dataset.stuck = this.#isStuck ? 'true' : 'false';
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();

        // The morph can swap out the buy-buttons block and change the bar's
        // own height (the stepper only renders for available variants), so
        // both inputs to the visibility rule need re-reading. The cached
        // nodes are re-resolved lazily; this just forces the recompute.
        this.#scheduleVisibilityUpdate();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      const targetAddToCartButton = this.#getTargetAddToCartButton();
      if (targetAddToCartButton) {
        targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#isStuck) return;
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    if (!this.#isStuck) return;
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * The buy-buttons block in the main buy box.
   *
   * Re-resolved whenever the cached node has been detached: a variant change
   * morphs the product form, and geometry read off a detached node reports an
   * all-zero rect, which would silently pin the bar to one state forever.
   *
   * @returns {Element | null}
   */
  #getBuyButtonsBlock() {
    if (!this.#buyButtonsBlock?.isConnected) {
      this.#buyButtonsBlock = this.#getProductForm()?.closest('.buy-buttons-block') ?? null;
    }
    return this.#buyButtonsBlock;
  }

  /**
   * How many pixels of the top of the viewport the header is currently
   * covering — the line the buy buttons actually vanish behind.
   *
   * `Math.max(0, …)` makes this self-correcting across the header's sticky
   * modes without having to read its state: while it is pinned, its bottom
   * edge sits a header-height down the viewport and that is the overlap;
   * once it stops being pinned it scrolls away with the page, its bottom goes
   * negative, and the overlap collapses to 0 — which is correct, because then
   * nothing is covering the top of the viewport.
   *
   * @returns {number}
   */
  #getHeaderOverlap() {
    if (!this.#header?.isConnected) {
      this.#header = document.querySelector('.header-section') ?? document.querySelector('#header-component');
    }
    if (!this.#header) return 0;
    return Math.max(0, this.#header.getBoundingClientRect().bottom);
  }

  /**
   * The page footer, re-resolved on the same terms as the buy-buttons block.
   * @returns {Element | null}
   */
  #getFooter() {
    if (!this.#footer?.isConnected) {
      // In themes migrated from 2.0, the footer element doesn't exist
      this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    }
    return this.#footer;
  }

  /**
   * The main buy box's add-to-cart button, which this bar puppets.
   * @returns {HTMLButtonElement | null}
   */
  #getTargetAddToCartButton() {
    if (!this.#targetAddToCartButton?.isConnected) {
      this.#targetAddToCartButton = /** @type {HTMLButtonElement | null} */ (
        this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null
      );
    }
    return this.#targetAddToCartButton;
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, stickyQuantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();
    if (stickyQuantityNumber) stickyQuantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
